import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../lib/axiosInstance";

const ROLE_LABEL = { doctor: "Doctor", staff: "Staff" };
const STAFF_ALLOWED_MODULES = new Set([
  "dashboard",
  "appointments",
  "billing",
  "patientrecords",
  "schedule",
]);

const emptyForm = () => ({
  name: "",
  email: "",
  password: "",
  role: "staff",
  permissions: [],
});

function buildPermissionRows(modules, existing) {
  const map = new Map(
    (existing || []).map((p) => [p.module, { view: !!p.view, edit: !!p.edit }])
  );
  return (modules || []).map((m) => {
    const cur = map.get(m.key) || { view: false, edit: false };
    return { module: m.key, label: m.label, view: cur.view, edit: cur.edit };
  });
}

function normalizeModuleKey(moduleKey) {
  return String(moduleKey || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function enforceStaffRules(rows) {
  return (rows || [])
    .filter((p) => STAFF_ALLOWED_MODULES.has(normalizeModuleKey(p.module)))
    .map((p) =>
      normalizeModuleKey(p.module) === "schedule"
        ? { ...p, view: true, edit: false }
        : p
    );
}

export default function AdminStaffPanel() {
  const [modules, setModules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [resetRow, setResetRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const buildRolePermissionRows = useCallback(
    (role, existingPermissions) => {
      const rows = buildPermissionRows(modules, existingPermissions);
      return role === "staff" ? enforceStaffRules(rows) : rows;
    },
    [modules]
  );

  const load = useCallback(async () => {
    setError("");
    try {
      const ts = Date.now();
      const [modRes, staffRes] = await Promise.all([
        axiosInstance.get("/admin/modules", { params: { _ts: ts } }),
        axiosInstance.get("/admin/staff", { params: { _ts: ts } }),
      ]);
      setModules(modRes.data?.modules || []);
      const raw =
        staffRes.data?.staff ?? staffRes.data?.items ?? staffRes.data;
      setStaff(Array.isArray(raw) ? raw : []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({
      ...emptyForm(),
      permissions: buildRolePermissionRows("staff", []),
    });
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      name: row.name || "",
      email: row.email || "",
      password: "",
      role: row.role === "doctor" ? "doctor" : "staff",
      permissions: buildRolePermissionRows(
        row.role === "doctor" ? "doctor" : "staff",
        row.permissions
      ),
    });
  };

  const updatePerm = (moduleKey, field, checked) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.map((p) =>
        p.module === moduleKey
          ? (f.role === "staff" && normalizeModuleKey(p.module) === "schedule")
            ? { ...p, view: true, edit: false }
            : {
              ...p,
              [field]: checked,
              ...(field === "view" && !checked ? { edit: false } : {}),
            }
          : p
      ),
    }));
  };

  const permissionsPayload = () =>
    (form.role === "staff" ? enforceStaffRules(form.permissions) : form.permissions).map(({ module, view, edit }) => ({
      module,
      view: !!view,
      edit: !!edit,
    }));

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/admin/staff", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        permissions: permissionsPayload(),
      });
      setCreateOpen(false);
      setForm(emptyForm());
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editRow) return;
    setSaving(true);
    setError("");
    try {
      await axiosInstance.patch(`/admin/staff/${editRow._id || editRow.id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        permissions: permissionsPayload(),
        isActive:
          typeof editRow.isActive === "boolean" ? editRow.isActive : true,
      });
      setEditRow(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row, isActive) => {
    setError("");
    const rowId = String(row._id || row.id);
    try {
      await axiosInstance.patch(`/admin/staff/${row._id || row.id}`, {
        isActive,
      });
      setStaff((prev) =>
        prev.map((s) =>
          String(s._id || s.id) === rowId ? { ...s, isActive } : s
        )
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Update failed");
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetRow || !form.password) return;
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post(
        `/admin/staff/${resetRow._id || resetRow.id}/reset-password`,
        { password: form.password }
      );
      setResetRow(null);
      setForm(emptyForm());
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="settings-muted">Loading staff…</p>;
  }

  return (
    <div className="admin-staff-panel">
      {error && (
        <div className="settings-banner settings-banner-error" role="alert">
          {error}
        </div>
      )}

      <div className="admin-staff-toolbar">
        <button
          type="button"
          className="settings-btn-primary"
          onClick={openCreate}
        >
          Add login
        </button>
      </div>

      <div className="settings-table-wrap">
        <table className="settings-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 ? (
              <tr>
                <td colSpan={5} className="settings-muted">
                  No doctor or staff logins yet.
                </td>
              </tr>
            ) : (
              staff.map((row) => (
                <tr key={row._id || row.id}>
                  <td>{row.name}</td>
                  <td>{row.email}</td>
                  <td>{ROLE_LABEL[row.role] || row.role}</td>
                  <td>
                    {row.isActive === false ? (
                      <span className="admin-staff-badge inactive">Inactive</span>
                    ) : (
                      <span className="admin-staff-badge active">Active</span>
                    )}
                  </td>
                  <td className="admin-staff-actions">
                    <button
                      type="button"
                      className="settings-btn-ghost"
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="settings-btn-ghost"
                      onClick={() => {
                        setResetRow(row);
                        setForm({ ...emptyForm(), password: "" });
                      }}
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      className="settings-btn-ghost"
                      onClick={() =>
                        toggleActive(row, row.isActive === false)
                      }
                    >
                      {row.isActive === false ? "Activate" : "Deactivate"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div
          className="settings-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-create-title"
        >
          <div className="settings-modal settings-modal-wide">
            <h3 id="admin-create-title">New login</h3>
            <form onSubmit={submitCreate} className="admin-staff-form">
              <label>
                Name
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                  required
                  minLength={6}
                />
              </label>
              <label>
                Role
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => {
                      const nextRole = e.target.value;
                      return {
                        ...f,
                        role: nextRole,
                        permissions: buildRolePermissionRows(nextRole, f.permissions),
                      };
                    })
                  }
                >
                  <option value="staff">Staff</option>
                  <option value="doctor">Doctor</option>
                </select>
              </label>
              <fieldset className="admin-perm-grid">
                <legend>Module access</legend>
                <div className="admin-perm-head">
                  <span>Module</span>
                  <span>View</span>
                  <span>Edit</span>
                </div>
                {form.permissions.map((p) => (
                  <div key={p.module} className="admin-perm-row">
                    <span>{p.label || p.module}</span>
                    <input
                      type="checkbox"
                      checked={p.view}
                      disabled={form.role === "staff" && normalizeModuleKey(p.module) === "schedule"}
                      onChange={(e) =>
                        updatePerm(p.module, "view", e.target.checked)
                      }
                    />
                    <input
                      type="checkbox"
                      checked={p.edit}
                      disabled={!p.view || (form.role === "staff" && normalizeModuleKey(p.module) === "schedule")}
                      onChange={(e) =>
                        updatePerm(p.module, "edit", e.target.checked)
                      }
                    />
                  </div>
                ))}
              </fieldset>
              <div className="settings-modal-actions">
                <button
                  type="button"
                  className="settings-btn-ghost"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settings-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editRow && (
        <div
          className="settings-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-edit-title"
        >
          <div className="settings-modal settings-modal-wide">
            <h3 id="admin-edit-title">Edit login</h3>
            <form onSubmit={submitEdit} className="admin-staff-form">
              <label>
                Name
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Role
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => {
                      const nextRole = e.target.value;
                      return {
                        ...f,
                        role: nextRole,
                        permissions: buildRolePermissionRows(nextRole, f.permissions),
                      };
                    })
                  }
                >
                  <option value="staff">Staff</option>
                  <option value="doctor">Doctor</option>
                </select>
              </label>
              <fieldset className="admin-perm-grid">
                <legend>Module access</legend>
                <div className="admin-perm-head">
                  <span>Module</span>
                  <span>View</span>
                  <span>Edit</span>
                </div>
                {form.permissions.map((p) => (
                  <div key={p.module} className="admin-perm-row">
                    <span>{p.label || p.module}</span>
                    <input
                      type="checkbox"
                      checked={p.view}
                      disabled={form.role === "staff" && normalizeModuleKey(p.module) === "schedule"}
                      onChange={(e) =>
                        updatePerm(p.module, "view", e.target.checked)
                      }
                    />
                    <input
                      type="checkbox"
                      checked={p.edit}
                      disabled={!p.view || (form.role === "staff" && normalizeModuleKey(p.module) === "schedule")}
                      onChange={(e) =>
                        updatePerm(p.module, "edit", e.target.checked)
                      }
                    />
                  </div>
                ))}
              </fieldset>
              <div className="settings-modal-actions">
                <button
                  type="button"
                  className="settings-btn-ghost"
                  onClick={() => setEditRow(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settings-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetRow && (
        <div
          className="settings-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-reset-title"
        >
          <div className="settings-modal">
            <h3 id="admin-reset-title">Reset password</h3>
            <p className="settings-muted">
              {resetRow.name} ({resetRow.email})
            </p>
            <form onSubmit={submitReset}>
              <label>
                New password
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                  required
                  minLength={6}
                />
              </label>
              <div className="settings-modal-actions">
                <button
                  type="button"
                  className="settings-btn-ghost"
                  onClick={() => setResetRow(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settings-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Updating…" : "Reset"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
